import { FC, useEffect, useMemo, useState } from 'react';
import { fetchSyncLogs, SyncLog } from '../api/logs';
import PaginationBar from '../components/PaginationBar';

const Logs: FC = () => {
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'' | 'pending' | 'success' | 'failed'>('');
  const [selectedLog, setSelectedLog] = useState<SyncLog | null>(null);
  const PAGE_LIMIT = 50;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchSyncLogs({ page, limit: PAGE_LIMIT, status: statusFilter || undefined })
      .then((resp) => { if (active) { setLogs(resp.data); setTotal(resp.total); setTotalPages(resp.totalPages); } })
      .catch((err) => { if (active) setError(err instanceof Error ? err.message : 'Failed to load logs.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, statusFilter]);

  const totalPagesMemo = useMemo(() => Math.max(1, totalPages), [totalPages]);

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <h1 className="text-2xl font-semibold text-slate-800 mb-4">Sync Logs</h1>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Filter by status:</span>
          <select
            className="rounded-md border border-slate-300 px-3 py-1 text-sm text-slate-700"
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value as any); }}
            disabled={loading}
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          {statusFilter && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
              onClick={() => {
                setStatusFilter('');
                setPage(1);
              }}
            >
              <span>✕</span> Clear Filter
            </button>
          )}
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">When</th>
              <th className="px-5 py-3 font-semibold">Endpoint</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Code</th>
              <th className="px-5 py-3 font-semibold">Payload/Message</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-5 py-6 text-center text-slate-400" colSpan={5}>Loading…</td></tr>
            ) : error ? (
              <tr><td className="px-5 py-6 text-center text-danger" colSpan={5}>{error}</td></tr>
            ) : logs.length === 0 ? (
              <tr><td className="px-5 py-6 text-center text-slate-400" colSpan={5}>No logs yet.</td></tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 text-slate-500">{new Date(log.createdAt).toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-700">{log.erpEndpoint}</td>
                  <td className="px-5 py-3">{log.status}</td>
                  <td className="px-5 py-3">{log.responseStatusCode ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500 max-w-[40rem] truncate">
                    {log.errorMessage ? (
                      <button
                        type="button"
                        className="text-primary underline underline-offset-2"
                        onClick={() => setSelectedLog(log)}
                      >
                        View details
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar
        page={page}
        totalPages={totalPagesMemo}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPagesMemo, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={<span>Showing {(page - 1) * PAGE_LIMIT + Math.min(PAGE_LIMIT, logs.length)} of {total} logs</span>}
      />
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Sync Log Details</h2>
                <p className="text-xs text-slate-500">
                  {new Date(selectedLog.createdAt).toLocaleString()} · {selectedLog.erpEndpoint}
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
                onClick={() => setSelectedLog(null)}
              >
                Close
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto px-6 py-4 text-sm">
              <div className="mb-2 text-slate-600">
                <span className="font-semibold">Status:</span> {selectedLog.status}{' '}
                {selectedLog.responseStatusCode != null && (
                  <span className="text-slate-500">({selectedLog.responseStatusCode})</span>
                )}
              </div>
              <pre className="whitespace-pre-wrap break-words rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-700">
                {selectedLog.errorMessage ?? 'No additional details.'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;
